import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Star, 
  Calendar, 
  Plus, 
  Check, 
  Play, 
  ExternalLink,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Heart,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { getTvDetails, getSeasonDetails, getImageUrl, getWatchProviderLogo, getProviderUrl, addSeries, getUserSeries, updateSeries, markEpisodeWatched, markBulkEpisodesWatched, unmarkEpisodeWatched, getSimilar } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const SeasonSection = ({ tvId, season, userEntry, onEpisodeToggle, onBulkMark, allSeasons }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEpisodes = async () => {
    if (episodes.length > 0) return;
    
    setLoading(true);
    try {
      const data = await getSeasonDetails(tvId, season.season_number);
      setEpisodes(data.episodes || []);
    } catch (error) {
      console.error('Error loading episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    if (!isOpen) {
      loadEpisodes();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkSeasonWatched = (e) => {
    e.stopPropagation();
    if (!episodes.length) {
      loadEpisodes().then(() => {
        onBulkMark(season.season_number, null, episodes);
      });
    } else {
      onBulkMark(season.season_number, null, episodes);
    }
  };

  const isEpisodeWatched = (episodeNumber) => {
    return userEntry?.watched_episodes?.some(
      ep => ep.season === season.season_number && ep.episode === episodeNumber
    );
  };

  const watchedInSeason = userEntry?.watched_episodes?.filter(
    ep => ep.season === season.season_number
  ).length || 0;
  const seasonProgress = season.episode_count > 0 
    ? Math.round((watchedInSeason / season.episode_count) * 100) 
    : 0;
  const isSeasonComplete = seasonProgress >= 100;

  return (
    <Collapsible open={isOpen} onOpenChange={handleOpen}>
      <CollapsibleTrigger asChild>
        <div 
          className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 cursor-pointer transition-colors"
          data-testid={`season-${season.season_number}`}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {season.poster_path && (
              <img 
                src={getImageUrl(season.poster_path, 'w92')}
                alt={season.name}
                className="w-16 h-24 rounded object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <h3 className="font-semibold">{season.name}</h3>
              <p className="text-sm text-gray-400">{season.episode_count} afleveringen</p>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-24">
                  <Progress value={seasonProgress} className="h-2" />
                </div>
                <span className="text-xs text-gray-400">{watchedInSeason}/{season.episode_count}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Mark Season Watched Button */}
            <button
              onClick={handleMarkSeasonWatched}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isSeasonComplete
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
              }`}
              title={isSeasonComplete ? 'Seizoen voltooid' : 'Markeer seizoen als gekeken'}
            >
              <Check className="w-5 h-5" />
            </button>
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent>
        <div className="mt-2 space-y-2 pl-4">
          {loading ? (
            <div className="p-4 text-center">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            episodes.map(episode => {
              const watched = isEpisodeWatched(episode.episode_number);
              
              return (
                <div 
                  key={episode.id}
                  className={`flex gap-4 p-3 rounded-lg transition-colors ${
                    watched ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/5 border border-white/5'
                  }`}
                >
                  {/* Episode Still */}
                  {episode.still_path && (
                    <img 
                      src={getImageUrl(episode.still_path, 'w185')}
                      alt={episode.name}
                      className="w-32 h-18 rounded object-cover flex-shrink-0 hidden sm:block"
                    />
                  )}
                  
                  {/* Episode Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-1">Aflevering {episode.episode_number}</p>
                        <h4 className="font-medium line-clamp-1 mb-1">{episode.name}</h4>
                        {episode.overview && (
                          <p className="text-sm text-gray-400 line-clamp-2">{episode.overview}</p>
                        )}
                        {episode.runtime && (
                          <p className="text-xs text-gray-500 mt-1">{episode.runtime} min</p>
                        )}
                      </div>
                      
                      <button
                        onClick={() => onEpisodeToggle(season.season_number, episode.episode_number, !watched, episodes, allSeasons)}
                        data-testid={`episode-${season.season_number}-${episode.episode_number}`}
                        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          watched 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const TvDetailPage = () => {
  const { id } = useParams();
  const { isAuthenticated, token } = useAuth();
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEntry, setUserEntry] = useState(null);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [similar, setSimilar] = useState([]);

  const fetchUserEntry = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const userSeries = await getUserSeries();
      const existing = userSeries.series?.find(s => s.tmdb_id === parseInt(id));
      setUserEntry(existing);
      return existing;
    } catch (e) {
      console.error('Error fetching user series:', e);
      return null;
    }
  }, [isAuthenticated, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const showData = await getTvDetails(id);
        setShow(showData);
        
        // Fetch similar shows
        try {
          const similarResponse = await getSimilar('tv', id);
          setSimilar(similarResponse.results || []);
        } catch (e) {
          console.log('Could not fetch similar shows');
        }

        const existing = await fetchUserEntry();

        // Fix possible mismatch between stored `total_episodes` and TMDB season totals.
        // This can happen when a series was added with a wrong total, or when totals changed.
        if (existing && showData?.seasons) {
          const seasons = showData.seasons.filter(s => s.season_number > 0 && (s.episode_count || 0) > 0);
          const computedTotal = seasons.reduce((sum, s) => sum + (s.episode_count || 0), 0);
          if (computedTotal > 0 && existing.total_episodes !== computedTotal) {
            try {
              await updateSeries(existing.id, { total_episodes: computedTotal });
              setUserEntry(prev => (prev ? { ...prev, total_episodes: computedTotal } : prev));
            } catch (e) {
              // non-fatal: UI can still render; Series page will remain inconsistent until next update
            }
          }
        }
      } catch (error) {
        console.error('Error fetching show:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isAuthenticated, fetchUserEntry]);

  const handleAddToLibrary = async (status = 'watchlist') => {
    if (!isAuthenticated) {
      toast.error('Log in om series toe te voegen');
      return null;
    }

    try {
      const totalEpisodes = show.seasons
        ?.filter(s => s.season_number > 0 && (s.episode_count || 0) > 0)
        .reduce((sum, s) => sum + (s.episode_count || 0), 0) || 0;

      const result = await addSeries({
        tmdb_id: show.id,
        title: show.name,
        poster_path: show.poster_path,
        backdrop_path: show.backdrop_path,
        status: status,
        total_episodes: totalEpisodes,
        episode_runtime: show.episode_run_time?.[0] || 45,
        genres: show.genres?.map(g => g.name) || []
      });
      setUserEntry(result.series);
      toast.success(status === 'watchlist' ? 'Toegevoegd aan watchlist' : 'Serie toegevoegd aan bibliotheek');
      return result.series;
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Serie staat al in je bibliotheek');
      } else {
        toast.error('Kon serie niet toevoegen');
      }
      return null;
    }
  };

  const handleEpisodeToggle = async (season, episode, shouldWatch, episodesList, allSeasons) => {
    if (!isAuthenticated) {
      toast.error('Log in om afleveringen te markeren');
      return;
    }

    let currentEntry = userEntry;

    // Auto-add to library if not exists
    if (!currentEntry) {
      currentEntry = await handleAddToLibrary('watching');
      if (!currentEntry) return;
    }

    // Check if we need to mark previous episodes
    if (shouldWatch) {
      const previousEpisodes = [];
      
      // Get all previous seasons
      for (const s of allSeasons.filter(s => s.season_number > 0 && s.season_number < season)) {
        for (let ep = 1; ep <= s.episode_count; ep++) {
          const isWatched = currentEntry?.watched_episodes?.some(
            e => e.season === s.season_number && e.episode === ep
          );
          if (!isWatched) {
            previousEpisodes.push({ season: s.season_number, episode: ep });
          }
        }
      }
      
      // Get previous episodes in current season
      for (let ep = 1; ep < episode; ep++) {
        const isWatched = currentEntry?.watched_episodes?.some(
          e => e.season === season && e.episode === ep
        );
        if (!isWatched) {
          previousEpisodes.push({ season, episode: ep });
        }
      }

      if (previousEpisodes.length > 0) {
        setConfirmDialog({
          open: true,
          data: {
            mode: 'episode',
            season,
            episode,
            previousEpisodes,
            currentEntry
          }
        });
        return;
      }
    }

    // Mark single episode
    try {
      if (shouldWatch) {
        await markEpisodeWatched(currentEntry.id, season, episode, show.id);
        await fetchUserEntry();
      } else {
        await unmarkEpisodeWatched(currentEntry.id, season, episode, show.id);
        await fetchUserEntry();
      }
    } catch (error) {
      toast.error('Kon aflevering niet bijwerken');
    }
  };

  const handleConfirmBulkMark = async (markPrevious) => {
    const {
      mode,
      season,
      episode,
      seasonNumber,
      episodesToMark,
      previousEpisodes,
      currentEntry
    } = confirmDialog.data;
    setConfirmDialog({ open: false, data: null });

    try {
      if (mode === 'season') {
        const target = episodesToMark || [];
        if (markPrevious) {
          const allEpisodes = [...(previousEpisodes || []), ...target];
          await markBulkEpisodesWatched(currentEntry.id, allEpisodes);
          await fetchUserEntry();
          toast.success(`${allEpisodes.length} afleveringen gemarkeerd als gekeken`);
        } else {
          await markBulkEpisodesWatched(currentEntry.id, target);
          await fetchUserEntry();
          toast.success(`Seizoen ${seasonNumber} gemarkeerd als gekeken`);
        }
      } else {
        // mode === 'episode'
        if (markPrevious) {
          const allEpisodes = [...(previousEpisodes || []), { season, episode }];
          await markBulkEpisodesWatched(currentEntry.id, allEpisodes);
          await fetchUserEntry();
          toast.success(`${allEpisodes.length} afleveringen gemarkeerd als gekeken`);
        } else {
          await markEpisodeWatched(currentEntry.id, season, episode, show.id);
          await fetchUserEntry();
        }
      }
    } catch (error) {
      toast.error('Kon afleveringen niet markeren');
    }
  };

  const handleBulkMarkSeason = async (seasonNumber, _, episodes) => {
    if (!isAuthenticated) {
      toast.error('Log in om afleveringen te markeren');
      return;
    }

    let currentEntry = userEntry;
    if (!currentEntry) {
      currentEntry = await handleAddToLibrary('watching');
      if (!currentEntry) return;
    }

    try {
      const episodesToMark = [];
      const seasonEpisodeCount = show.seasons?.find(s => s.season_number === seasonNumber)?.episode_count || 0;
      
      for (let ep = 1; ep <= seasonEpisodeCount; ep++) {
        const isWatched = currentEntry?.watched_episodes?.some(
          e => e.season === seasonNumber && e.episode === ep
        );
        if (!isWatched) {
          episodesToMark.push({ season: seasonNumber, episode: ep });
        }
      }

      if (episodesToMark.length === 0) {
        toast.info('Seizoen is al volledig gekeken');
        return;
      }

      // Ask if user also wants to mark previous seasons/episodes
      const previousEpisodes = [];
      const prevSeasons = (show.seasons || []).filter(
        s => s.season_number > 0 && (s.episode_count || 0) > 0 && s.season_number < seasonNumber
      );

      for (const s of prevSeasons) {
        for (let ep = 1; ep <= (s.episode_count || 0); ep++) {
          const isWatched = currentEntry?.watched_episodes?.some(
            e => e.season === s.season_number && e.episode === ep
          );
          if (!isWatched) {
            previousEpisodes.push({ season: s.season_number, episode: ep });
          }
        }
      }

      if (previousEpisodes.length > 0) {
        setConfirmDialog({
          open: true,
          data: {
            mode: 'season',
            seasonNumber,
            episodesToMark,
            previousEpisodes,
            currentEntry
          }
        });
        return;
      }

      await markBulkEpisodesWatched(currentEntry.id, episodesToMark);
      await fetchUserEntry();
      toast.success(`Seizoen ${seasonNumber} gemarkeerd als gekeken`);
    } catch (error) {
      toast.error('Kon seizoen niet markeren');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!show) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Serie niet gevonden</p>
      </div>
    );
  }

  const backdrop = getImageUrl(show.backdrop_path, 'w1280');
  const watchProviders = show.watch_providers_nl;
  const trailer = show.videos?.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube');
  const seasons = show.seasons?.filter(s => s.season_number > 0 && (s.episode_count || 0) > 0) || [];
  
  const totalEpisodes = seasons.reduce((sum, s) => sum + (s.episode_count || 0), 0);
  const watchedEpisodes = userEntry?.watched_episodes?.length || 0;
  const overallProgress = totalEpisodes > 0 ? Math.round((watchedEpisodes / totalEpisodes) * 100) : 0;

  return (
    <div data-testid="tv-detail-page" className="min-h-screen">
      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, data: null })}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogTitle>
            {confirmDialog.data?.mode === 'season'
              ? 'Eerdere seizoenen ook markeren?'
              : 'Voorgaande afleveringen markeren?'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {confirmDialog.data?.mode === 'season'
              ? `Wil je ook alle ${confirmDialog.data?.previousEpisodes?.length || 0} afleveringen uit eerdere seizoenen als gekeken markeren?`
              : `Wil je ook alle ${confirmDialog.data?.previousEpisodes?.length || 0} voorgaande afleveringen als gekeken markeren?`}
          </DialogDescription>
          <DialogFooter className="gap-2">
            <Button variant="secondary" onClick={() => handleConfirmBulkMark(false)}>
              {confirmDialog.data?.mode === 'season' ? 'Alleen dit seizoen' : 'Alleen deze'}
            </Button>
            <Button onClick={() => handleConfirmBulkMark(true)} className="bg-indigo-600 hover:bg-indigo-700">
              {confirmDialog.data?.mode === 'season' ? 'Ja, ook eerdere seizoenen' : 'Ja, allemaal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trailer Modal */}
      <Dialog open={trailerOpen} onOpenChange={setTrailerOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-gray-800">
          <div className="relative pt-[56.25%]">
            <button
              onClick={() => setTrailerOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            {trailer && (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${trailer.key}?autoplay=1`}
                title={`${show.name} Trailer`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          {backdrop && (
            <img src={backdrop} alt={show.name} className="w-full h-full object-cover" />
          )}
          {/* Lighter gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-[#050505]/40 to-transparent" />
        </div>

        <Link 
          to="/"
          className="absolute top-4 left-4 md:top-8 md:left-8 z-20 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Terug</span>
        </Link>

        <div className="relative h-full flex items-end">
          <div className="p-6 md:p-12 w-full max-w-4xl">
            {/* Logo or Title */}
            {show.logo_url ? (
              <img 
                src={show.logo_url} 
                alt={show.name}
                className="h-20 md:h-28 lg:h-32 object-contain mb-4"
              />
            ) : (
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold tracking-tight text-white mb-4">
                {show.name}
              </h1>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300 mb-4">
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                {show.vote_average?.toFixed(1)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {show.first_air_date?.slice(0, 4)} - {show.status === 'Ended' ? show.last_air_date?.slice(0, 4) : 'Heden'}
              </span>
              <span>{seasons.length} seizoenen</span>
              <span>{totalEpisodes} afleveringen</span>
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-6">
              {show.genres?.map(genre => (
                <Badge 
                  key={genre.id}
                  variant="secondary"
                  className="bg-white/10 text-white border-none"
                >
                  {genre.name}
                </Badge>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {trailer && (
                <Button 
                  data-testid="watch-trailer-btn"
                  onClick={() => setTrailerOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-6 py-5"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Trailer
                </Button>
              )}

              {!userEntry && (
                <>
                  <Button 
                    data-testid="add-watchlist-btn"
                    variant="secondary"
                    onClick={() => handleAddToLibrary('watchlist')}
                    className="bg-white/10 hover:bg-white/20 rounded-full px-6 py-5"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Watchlist
                  </Button>
                  <Button 
                    data-testid="add-library-btn"
                    variant="secondary"
                    onClick={() => handleAddToLibrary('watching')}
                    className="bg-white/10 hover:bg-white/20 rounded-full px-6 py-5"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Begin met kijken
                  </Button>
                </>
              )}

              {userEntry?.status === 'watchlist' && (
                <Button 
                  data-testid="in-watchlist-btn"
                  variant="secondary"
                  className="bg-pink-600/20 text-pink-400 border-pink-500/30 rounded-full px-6 py-5"
                  disabled
                >
                  <Heart className="w-4 h-4 mr-2 fill-current" />
                  Op watchlist
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Progress Bar */}
      {userEntry && (
        <section className="p-6 md:px-12 -mt-4">
          <div className="glass-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Voortgang</span>
              <span className="text-sm font-medium">{watchedEpisodes}/{totalEpisodes} afleveringen ({overallProgress}%)</span>
            </div>
            <Progress value={overallProgress} className="h-3" />
          </div>
        </section>
      )}

      {/* Details Section */}
      <section className="p-6 md:p-12 max-w-6xl">
        {/* Overview */}
        {show.overview && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Samenvatting</h2>
            <p className="text-gray-300 leading-relaxed">{show.overview}</p>
          </div>
        )}

        {/* Watch Providers */}
        {watchProviders && (watchProviders.flatrate || watchProviders.rent) && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Waar te bekijken (Nederland)</h2>
            
            {watchProviders.flatrate && (
              <div className="flex flex-wrap gap-3">
                {watchProviders.flatrate.map(provider => (
                  <a
                    key={provider.provider_id}
                    href={getProviderUrl(provider.provider_id, watchProviders.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                  >
                    <img 
                      src={getWatchProviderLogo(provider.logo_path)} 
                      alt={provider.provider_name}
                      className="w-8 h-8 rounded"
                    />
                    <span className="text-sm">{provider.provider_name}</span>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cast */}
        {show.credits?.cast && show.credits.cast.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xl font-display font-bold mb-4">Cast</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar -mx-6 px-6 md:-mx-12 md:px-12">
              {show.credits.cast.slice(0, 20).map(person => (
                <Link 
                  key={person.id} 
                  to={`/person/${person.id}`}
                  className="flex-shrink-0 w-24 group"
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-800 mb-2 group-hover:ring-2 group-hover:ring-indigo-500 transition-all">
                    {person.profile_path ? (
                      <img 
                        src={getImageUrl(person.profile_path, 'w185')}
                        alt={person.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600 text-2xl font-bold">
                        {person.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-center line-clamp-1 group-hover:text-indigo-400 transition-colors">{person.name}</p>
                  <p className="text-xs text-gray-500 text-center line-clamp-1">{person.character}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Seasons */}
        <div className="mb-10">
          <h2 className="text-xl font-display font-bold mb-4">Seizoenen & Afleveringen</h2>
          <div className="space-y-3">
            {seasons.map(season => (
              <SeasonSection 
                key={season.id}
                tvId={show.id}
                season={season}
                userEntry={userEntry}
                onEpisodeToggle={handleEpisodeToggle}
                onBulkMark={handleBulkMarkSeason}
                allSeasons={seasons}
              />
            ))}
          </div>
        </div>

        {/* Similar Shows */}
        {similar.length > 0 && (
          <div>
            <h2 className="text-xl font-display font-bold mb-4">Vergelijkbare series</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {similar.slice(0, 16).map(rec => (
                <Link 
                  key={rec.id}
                  to={`/tv/${rec.id}`}
                  className="group aspect-[2/3] rounded-lg overflow-hidden bg-gray-900 hover:ring-2 hover:ring-indigo-500 transition-all"
                >
                  {rec.poster_path ? (
                    <img 
                      src={getImageUrl(rec.poster_path, 'w342')}
                      alt={rec.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-2">
                      <span className="text-xs text-gray-500 text-center">{rec.name}</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TvDetailPage;
